/**
 * Canvas renderer for the inference animation. `render(t)` draws the moment
 * `t` seconds into the timeline; apart from sparks, nothing here remembers the
 * previous frame.
 *
 * Visual language, kept consistent everywhere:
 * - a box that glows, shakes and throws sparks is doing heavy math
 * - dots running along a pipe are data moving to or from memory
 * - dots that collide and merge into one are information being compressed
 */
import {
  alongPolyline,
  clamp,
  cubic,
  easeInCubic,
  easeInOutCubic,
  easeOutBack,
  easeOutCubic,
  easeOutQuad,
  envelope,
  GlowSprites,
  hash,
  hexToRgb,
  lerp,
  mix,
  noise,
  prog,
  quad,
  rgba,
  Sparks,
  type Point,
  type RGB
} from "./fx";
import {
  attentionWeight,
  CANDIDATE_WEIGHTS,
  CANDIDATES,
  EXPERT_COUNT,
  LAYER_COUNT,
  loadAt,
  PROMPT,
  PROMPT_COUNT,
  ROUTES,
  ST_EMBED,
  ST_L1,
  ST_L2,
  ST_L3,
  ST_L90,
  ST_LM,
  ST_WARP,
  TOKENS,
  VOCAB_SIZE,
  type Interior,
  type ModelKind,
  type Pass,
  type Timeline
} from "./timeline";

export const W = 960;
export const H = 540;

export type Fonts = { pixel: string; mono: string };

/** The model switch in the HUD. The React layer puts real buttons over these rects. */
export const MODEL_TOGGLE = {
  y: 9,
  h: 20,
  options: [
    { model: "dense", label: "DENSE", name: "Dense model", x: 172, w: 56 },
    { model: "moe", label: "MOE", name: "Mixture-of-experts model", x: 232, w: 42 },
    { model: "linear", label: "LINEAR", name: "Linear-attention model", x: 278, w: 62 }
  ] as Array<{ model: ModelKind; label: string; name: string; x: number; w: number }>
};
type Rect = { x: number; y: number; w: number; h: number };
type Space = "overview" | "interior" | "screen";
type FxEvent = {
  t: number;
  space: Space;
  x: number;
  y: number;
  color: RGB;
  size: number;
  sparks: number;
  shake: number;
  big?: boolean;
};

const C = {
  panel: hexToRgb("#070c1c"),
  frame: hexToRgb("#1b2f5c"),
  idle: hexToRgb("#27477f"),
  label: hexToRgb("#6f8fcf"),
  text: hexToRgb("#d6e4ff"),
  dim: hexToRgb("#4a6397"),
  cell: hexToRgb("#111f3d"),
  memDot: hexToRgb("#1a3060"),
  packet: hexToRgb("#bfe9ff"),
  cyan: hexToRgb("#5cc8ff"),
  ember: hexToRgb("#ff5a1f"),
  hot: hexToRgb("#ff9a3d"),
  white: hexToRgb("#fff3dc"),
  gold: hexToRgb("#ffd23f"),
  red: hexToRgb("#ff3b5c")
};

const heatColor = (h: number): RGB => {
  if (h < 0.35) return mix(C.idle, C.ember, h / 0.35);
  if (h < 0.75) return mix(C.ember, C.hot, (h - 0.35) / 0.4);
  return mix(C.hot, C.white, (h - 0.75) / 0.25);
};

const display = (text: string) => (text.startsWith(" ") ? "·" + text.slice(1) : text);

// ---- Overview layout -------------------------------------------------------
const CONTEXT: Rect = { x: 20, y: 44, w: 920, h: 76 };
const CHIP_Y = 62;
const CHIP_H = 30;
const ROW_Y = 230;
const BOX_TOP = 188;
const BOX_H = 84;
const PIPE_TOP = BOX_TOP + BOX_H;
const HBM: Rect = { x: 22, y: 392, w: 772, h: 124 };
const SEG_TOP = 416;
const SEG_BOTTOM = 506;
const STATIONS: Array<{ x: number; w: number; label: string }> = [
  { x: 30, w: 76, label: "EMBED" },
  { x: 134, w: 88, label: "LAYER 1" },
  { x: 250, w: 88, label: "LAYER 2" },
  { x: 366, w: 88, label: "LAYER 3" },
  { x: 482, w: 64, label: "4-89" },
  { x: 574, w: 88, label: "LAYER 90" },
  { x: 690, w: 88, label: "LM HEAD" }
];
const LOGITS: Rect = { x: 806, y: 136, w: 124, h: 244 };
const LOGIT_ROW_Y = (r: number) => 180 + r * 30;
const BAR_X = 874;
const BAR_MAX = 48;

const stationCx = (s: number) => STATIONS[s].x + STATIONS[s].w / 2;
const hasKv = (s: number) => s >= ST_L1 && s <= ST_L90;
const weightsBlock = (s: number): Rect => {
  const { x, w } = STATIONS[s];
  return hasKv(s)
    ? { x, y: SEG_TOP, w: w * 0.56, h: SEG_BOTTOM - SEG_TOP }
    : { x, y: SEG_TOP, w, h: SEG_BOTTOM - SEG_TOP };
};
const kvBlock = (s: number): Rect => {
  const { x, w } = STATIONS[s];
  return { x: x + w * 0.6, y: SEG_TOP, w: w * 0.4, h: SEG_BOTTOM - SEG_TOP };
};
const weightsPipeX = (s: number) => {
  const b = weightsBlock(s);
  return b.x + b.w / 2;
};
const kvPipeX = (s: number) => {
  const b = kvBlock(s);
  return b.x + b.w / 2;
};

// ---- Interior (zoomed-in layer 1) layout -----------------------------------
const PANEL: Rect = { x: 20, y: 128, w: 920, h: 394 };
const I_Y = (i: number) => 180 + i * 32;
const I_IN_X = 112;
const I_ATTN_X = 330;
const I_ROUTER_X = 467;
const I_MERGE_X = 860;
const I_EXIT_X = 975;
const ATTN: Rect = { x: 150, y: 162, w: 250, h: 230 };
const ROUTER: Rect = { x: 455, y: 162, w: 24, h: 230 };
const EXPERT_W = 38;
const EXPERT_TOP = 402;
const EXPERT_H = 40;
const expertX = (e: number) => 500 + e * (EXPERT_W + 7.4);
const expertCx = (e: number) => expertX(e) + EXPERT_W / 2;
const EXPERT_CY = EXPERT_TOP + EXPERT_H / 2;
const I_MEM_TOP = 458;
const I_MEM_BOTTOM = 514;
const I_KV: Rect = { x: 150, y: I_MEM_TOP, w: 105, h: I_MEM_BOTTOM - I_MEM_TOP };
const I_ATTN_W: Rect = { x: 265, y: I_MEM_TOP, w: 135, h: I_MEM_BOTTOM - I_MEM_TOP };
const I_KV_PIPE_X = 202;
const I_W_PIPE_X = 332;
const kvColumnX = (i: number) => I_KV.x + 6 + i * 8.9;
const FFN: Rect = { x: 492, y: 162, w: 336, h: 230 };
const FFN_PIPES = [590, 730];
const STATE_GRID: Rect = { x: 176, y: 238, w: 78, h: 78 };
const STATE_CELLS = 6;

const MODEL_COLORS: Record<ModelKind, RGB> = {
  dense: hexToRgb("#7fd4ff"),
  moe: hexToRgb("#c77dff"),
  linear: hexToRgb("#5ef2b5")
};

export class Scene {
  private ctx: CanvasRenderingContext2D;
  private px = 1;
  private cssW = W;
  private cssH = H;
  private dpr = 1;
  private bg: HTMLCanvasElement | null = null;
  private scan: CanvasPattern | null = null;
  private glow = new GlowSprites();
  private sparks = new Sparks();
  private events: FxEvent[] = [];
  private fonts: Fonts = { pixel: "monospace", mono: "monospace" };
  private cw = 12;
  private chips: Array<{ x: number; w: number; cx: number; label: string }> = [];
  private spans: Array<{ x: number; w: number }> = [];
  private kvRows: Array<Array<{ token: number; land: number }>> = [];
  private kvPaths: Point[][] = [];
  private statePath: Point[] = [];
  private lastT = 0;
  private lastAmbient = 0;
  private uiSparks = new Sparks();
  private switchedAt = -10;
  private model: ModelKind = "moe";

  reducedMotion = false;
  /** Toggle option under the pointer or keyboard focus, for the hover glow. */
  hover: ModelKind | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    readonly tl: Timeline
  ) {
    this.ctx = canvas.getContext("2d");
    this.layoutChips();
    this.collectKvRows();
    this.buildKvPaths();
    this.buildEvents();
  }

  setFonts(fonts: Fonts) {
    this.fonts = fonts;
    this.layoutChips();
    this.buildEvents();
  }

  /** Swap architectures in place: same moment in the timeline, different machine. */
  setModel(model: ModelKind, celebrate = true) {
    if (model === this.model) return;
    this.model = model;
    this.buildEvents();
    if (!celebrate) return;
    this.switchedAt = this.lastAmbient;
    const opt = MODEL_TOGGLE.options.find((o) => o.model === model);
    if (!this.reducedMotion)
      this.uiSparks.burst(opt.x + opt.w / 2, MODEL_TOGGLE.y + MODEL_TOGGLE.h / 2, MODEL_COLORS[model], 22, 220, {
        gravity: 140,
        life: 0.6,
        size: 1.6
      });
  }

  resize(cssW: number, cssH: number, dpr: number) {
    this.cssW = cssW;
    this.cssH = cssH;
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.round(cssW * dpr));
    this.canvas.height = Math.max(1, Math.round(cssH * dpr));
    this.px = (cssW / W) * dpr;
    this.bg = null;
  }

  /** Call after a jump in time so sparks from the old moment don't linger. */
  seek(t: number) {
    this.sparks.clear();
    this.lastT = t;
  }

  // ---------------------------------------------------------------------------
  // Setup

  private layoutChips() {
    const ctx = this.ctx;
    ctx.font = `20px ${this.fonts.mono}`;
    this.cw = ctx.measureText("M").width || 12;
    let x = 40;
    this.chips = TOKENS.map((tok) => {
      const label = display(tok.text);
      const w = label.length * this.cw + 14;
      const chip = { x, w, cx: x + w / 2, label };
      x += w + 6;
      return chip;
    });
    let a = 0;
    this.spans = TOKENS.slice(0, PROMPT_COUNT).map((tok) => {
      const span = { x: 40 + a * this.cw - 7, w: tok.text.length * this.cw + 14 };
      a += tok.text.length;
      return span;
    });
  }

  private collectKvRows() {
    this.kvRows = STATIONS.map(() => []);
    for (const pass of this.tl.passes) {
      for (const run of pass.runs) {
        for (const w of run.kvWrites) this.kvRows[run.station].push({ token: w.token, land: w.leave + pass.pipeTime });
      }
    }
  }

  /**
   * Linear attention's fixed-size state: each cell shows the last token written
   * into it, fading as later tokens pile in. `folds` are (token, time) writes.
   */
  private stateCell(
    cell: number,
    folds: Array<{ token: number; at: number }>,
    t: number
  ): { color: RGB; a: number } | null {
    let writer = -1;
    let later = 0;
    for (let k = folds.length - 1; k >= 0; k--) {
      const f = folds[k];
      if (f.at > t) continue;
      if (hash(f.token, cell, 7) < 0.42) {
        writer = f.token;
        break;
      }
      later++;
    }
    if (writer < 0) return null;
    return { color: TOKENS[writer].color, a: 0.35 + 0.65 * Math.pow(0.8, later) };
  }

  /** Folds landed in a station's state, as the overview sees them. Prefill stores the state once, at the end. */
  private stationFolds(s: number): Array<{ token: number; at: number }> {
    const folds: Array<{ token: number; at: number }> = [];
    for (const pass of this.tl.passes) {
      const run = pass.runs.find((r) => r.station === s);
      if (!run || !run.kvWrites.length) continue;
      const stored = Math.max(...run.kvWrites.map((w) => w.leave)) + pass.pipeTime;
      for (const w of run.kvWrites) folds.push({ token: w.token, at: stored });
    }
    return folds;
  }

  private buildKvPaths() {
    const sFrom = { x: STATE_GRID.x + STATE_GRID.w / 2, y: STATE_GRID.y + STATE_GRID.h };
    this.statePath = [
      sFrom,
      { x: I_KV_PIPE_X, y: ATTN.y + ATTN.h },
      { x: I_KV_PIPE_X, y: I_MEM_TOP + 6 },
      { x: I_KV.x + I_KV.w / 2, y: I_MEM_TOP + 20 }
    ];
    this.kvPaths = [];
    for (let i = 0; i < PROMPT_COUNT; i++) {
      const pts: Point[] = [];
      const from = { x: I_ATTN_X, y: I_Y(i) };
      const ctrl = { x: 250, y: I_Y(i) + 30 };
      const to = { x: I_KV_PIPE_X, y: ATTN.y + ATTN.h };
      for (let k = 0; k <= 12; k++) pts.push(quad(from, ctrl, to, k / 12));
      pts.push({ x: I_KV_PIPE_X, y: I_MEM_TOP + 6 });
      pts.push({ x: kvColumnX(i) + 3.5, y: I_MEM_TOP + 16 });
      this.kvPaths.push(pts);
    }
  }

  private buildEvents() {
    const ev: FxEvent[] = [];
    const tl = this.tl;
    for (const pass of tl.passes) {
      const I = pass.interior;
      if (I && this.model === "linear") {
        const grid = { x: STATE_GRID.x + STATE_GRID.w / 2, y: STATE_GRID.y + STATE_GRID.h / 2 };
        for (let i = 0; i < PROMPT_COUNT; i++) {
          const fold = I.foldLaunch[i] + I.foldTravel;
          ev.push({ t: fold, space: "interior", ...grid, color: TOKENS[i].color, size: 0.8, sparks: 8, shake: 0.8 });
          ev.push({
            t: fold + 0.05 + I.readTravel,
            space: "interior",
            x: I_ATTN_X,
            y: I_Y(i),
            color: TOKENS[i].color,
            size: 0.8,
            sparks: 6,
            shake: 0.6
          });
        }
        ev.push({
          t: I.stateStore + I.kvTravel,
          space: "interior",
          x: I_KV.x + I_KV.w / 2,
          y: I_MEM_TOP + 30,
          color: C.packet,
          size: 0.7,
          sparks: 6,
          shake: 0
        });
      }
      if (I) {
        for (let i = 1; i < PROMPT_COUNT && this.model !== "linear"; i++) {
          ev.push({
            t: I.mixLaunch[i] + I.mixTravel,
            space: "interior",
            x: I_ATTN_X,
            y: I_Y(i),
            color: TOKENS[i].color,
            size: 1,
            sparks: 10,
            shake: 1.2
          });
        }
        for (let i = 0; i < PROMPT_COUNT; i++) {
          ev.push({
            t: I.expertOut[i] + I.returnTravel,
            space: "interior",
            x: I_MERGE_X,
            y: I_Y(i),
            color: TOKENS[i].color,
            size: 1,
            sparks: 10,
            shake: 1.2
          });
          if (this.model !== "linear")
            ev.push({
              t: I.kvLeave[i] + I.kvTravel,
              space: "interior",
              x: kvColumnX(i) + 3.5,
              y: I_MEM_TOP + 20,
              color: TOKENS[i].color,
              size: 0.45,
              sparks: 3,
              shake: 0
            });
        }
      }
      for (const lookup of pass.runs[0].lookups) {
        ev.push({
          t: lookup.at + pass.pipeTime,
          space: "overview",
          x: stationCx(ST_EMBED),
          y: this.slot(ST_EMBED, pass.tokens.indexOf(lookup.token), pass.tokens.length).y,
          color: TOKENS[lookup.token].color,
          size: 0.5,
          sparks: 5,
          shake: 0
        });
      }
      const winY = LOGIT_ROW_Y(0);
      // Screen space so the shockwave rolls over the context panel instead of under it.
      ev.push({
        t: pass.smash,
        space: "screen",
        x: 840,
        y: winY,
        color: TOKENS[pass.out].color,
        size: pass.kind === "prefill" ? 2.4 : 1.6,
        sparks: pass.kind === "prefill" ? 46 : 24,
        shake: pass.kind === "prefill" ? 7 : 3.5,
        big: true
      });
      const chip = this.chips[pass.out];
      ev.push({
        t: pass.land,
        space: "screen",
        x: chip.cx,
        y: CHIP_Y + CHIP_H / 2,
        color: TOKENS[pass.out].color,
        size: 1.1,
        sparks: 14,
        shake: 1.5
      });
    }
    ev.push({
      t: tl.decodeBanner + 0.12,
      space: "screen",
      x: W / 2,
      y: H / 2,
      color: C.gold,
      size: 0,
      sparks: 0,
      shake: 4
    });
    ev.push({
      t: tl.prefillBanner + 0.12,
      space: "screen",
      x: W / 2,
      y: H / 2,
      color: C.cyan,
      size: 0,
      sparks: 0,
      shake: 2.5
    });
    this.events = ev.sort((a, b) => a.t - b.t);
  }

  private renderBackground() {
    const bg = document.createElement("canvas");
    bg.width = this.canvas.width;
    bg.height = this.canvas.height;
    const g = bg.getContext("2d");
    const s = this.px;
    const grad = g.createLinearGradient(0, 0, 0, bg.height);
    grad.addColorStop(0, "#070b19");
    grad.addColorStop(1, "#02040a");
    g.fillStyle = grad;
    g.fillRect(0, 0, bg.width, bg.height);
    g.fillStyle = "rgba(40,70,140,0.22)";
    for (let y = 6; y < H; y += 12)
      for (let x = 6; x < W; x += 12) g.fillRect(x * s, y * s, Math.max(1, s * 0.9), Math.max(1, s * 0.9));
    const vig = g.createRadialGradient(
      bg.width / 2,
      bg.height / 2,
      bg.height * 0.3,
      bg.width / 2,
      bg.height / 2,
      bg.width * 0.7
    );
    vig.addColorStop(0, "rgba(0,0,0,0)");
    vig.addColorStop(1, "rgba(0,0,0,0.6)");
    g.fillStyle = vig;
    g.fillRect(0, 0, bg.width, bg.height);
    this.bg = bg;

    const line = document.createElement("canvas");
    line.width = 1;
    line.height = Math.max(2, Math.round(3 * this.dpr));
    const lg = line.getContext("2d");
    lg.fillStyle = "rgba(0,0,0,0.22)";
    lg.fillRect(0, 0, 1, Math.max(1, Math.round(this.dpr)));
    this.scan = this.ctx.createPattern(line, "repeat");
  }

  // ---------------------------------------------------------------------------
  // Frame

  render(t: number, dtSim: number, ambient: number) {
    const ctx = this.ctx;
    if (!this.bg) this.renderBackground();

    this.fireEvents(t, dtSim);
    this.sparks.update(dtSim);
    // Toggle sparks run on wall-clock time so they still fly while the timeline is paused.
    this.uiSparks.update(Math.min(0.05, Math.max(0, ambient - this.lastAmbient)));
    this.lastAmbient = ambient;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(this.bg, 0, 0);

    const shake = this.shake(t);
    const base = () => ctx.setTransform(this.px, 0, 0, this.px, shake.x * this.px, shake.y * this.px);
    const cam = this.camera(t);

    if (cam.overview > 0) {
      base();
      if (cam.zoom !== 1) {
        ctx.translate(cam.cx, cam.cy);
        ctx.scale(cam.zoom, cam.zoom);
        ctx.translate(-cam.cx, -cam.cy);
      }
      ctx.globalAlpha = cam.overview;
      this.drawOverview(t, dtSim * (cam.overview > 0.9 ? 1 : 0), ambient);
      this.drawEvents(t, "overview");
      ctx.globalAlpha = 1;
    }

    if (cam.panel > 0) {
      base();
      this.drawInterior(t, dtSim, cam.rect, cam.panel, cam.fill, cam.content);
    }

    base();
    this.drawContext(t);
    this.drawEvents(t, "screen");
    this.sparks.draw(ctx, this.glow);
    this.drawFlyingChip(t);
    this.drawEndCard(t);
    this.drawBanners(t);

    ctx.setTransform(this.px, 0, 0, this.px, 0, 0);
    this.drawHud(t, ambient);
    this.drawFlash(t);
    this.drawSwitch(ambient);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (this.scan) {
      ctx.fillStyle = this.scan;
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }
    this.lastT = t;
  }

  private fireEvents(t: number, dtSim: number) {
    const from = this.lastT;
    if (dtSim <= 0 || t < from || t - from > 0.5) return;
    const cam = this.camera(t);
    for (const e of this.events) {
      if (e.t <= from || e.t > t || !e.sparks || this.reducedMotion) continue;
      if (e.space === "interior" && cam.content < 0.5) continue;
      if (e.space === "overview" && cam.overview < 0.5) continue;
      this.sparks.burst(e.x, e.y, mix(e.color, C.white, 0.35), e.sparks, e.big ? 420 : 200, {
        gravity: 120,
        life: e.big ? 0.9 : 0.5,
        size: e.big ? 2.2 : 1.5
      });
    }
  }

  private shake(t: number): Point {
    if (this.reducedMotion) return { x: 0, y: 0 };
    let mag = 0;
    for (const e of this.events) {
      if (!e.shake || t < e.t || t > e.t + 0.45) continue;
      const k = 1 - (t - e.t) / 0.45;
      mag += e.shake * k * k;
    }
    return { x: mag * noise(t * 47, 1), y: mag * noise(t * 43, 2) };
  }

  private camera(t: number) {
    const I = this.tl.passes[0].interior;
    const l1 = STATIONS[ST_L1];
    const from: Rect = { x: l1.x, y: BOX_TOP, w: l1.w, h: BOX_H };
    const none = { zoom: 1, cx: 0, cy: 0, overview: 1, panel: 0, fill: 0, content: 0, rect: PANEL };
    if (t < I.zoomIn[0] || t > I.zoomOut[1]) return none;
    // e: 0 = overview, 1 = fully inside the layer
    let e: number;
    if (t < I.zoomIn[1]) e = prog(t, I.zoomIn[0], I.zoomIn[1] - I.zoomIn[0]);
    else if (t < I.zoomOut[0]) e = 1;
    else e = 1 - prog(t, I.zoomOut[0], I.zoomOut[1] - I.zoomOut[0]);
    const zoom = 1 + 2.4 * easeInCubic(e);
    const cx = stationCx(ST_L1);
    const cy = ROW_Y;
    const zoomed: Rect = {
      x: cx + (from.x - cx) * zoom,
      y: cy + (from.y - cy) * zoom,
      w: from.w * zoom,
      h: from.h * zoom
    };
    const grow = easeInOutCubic(clamp((e - 0.1) / 0.6));
    const rect: Rect = {
      x: lerp(zoomed.x, PANEL.x, grow),
      y: lerp(zoomed.y, PANEL.y, grow),
      w: lerp(zoomed.w, PANEL.w, grow),
      h: lerp(zoomed.h, PANEL.h, grow)
    };
    return {
      zoom,
      cx,
      cy,
      overview: 1 - clamp((e - 0.2) / 0.4),
      panel: clamp(e / 0.2),
      fill: clamp((e - 0.15) / 0.35),
      content: clamp((e - 0.45) / 0.35),
      rect
    };
  }

  // ---------------------------------------------------------------------------
  // Drawing primitives

  private text(
    s: string,
    x: number,
    y: number,
    size: number,
    color: RGB,
    alpha = 1,
    opts: { font?: "pixel" | "mono"; align?: CanvasTextAlign; glow?: number; weight?: number } = {}
  ) {
    if (alpha <= 0) return;
    const ctx = this.ctx;
    ctx.font = `${opts.weight ?? 400} ${size}px ${opts.font === "mono" ? this.fonts.mono : this.fonts.pixel}`;
    ctx.textAlign = opts.align ?? "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = rgba(color, alpha);
    if (opts.glow) {
      ctx.shadowColor = rgba(color, alpha * 0.9);
      ctx.shadowBlur = opts.glow * this.px;
    }
    ctx.fillText(s, x, y);
    ctx.shadowBlur = 0;
  }

  private rect(r: Rect, radius: number, fill: string | null, stroke: string | null, lineWidth = 1) {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.roundRect(r.x, r.y, r.w, r.h, radius);
    if (fill) {
      ctx.fillStyle = fill;
      ctx.fill();
    }
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    }
  }

  /** Neon glow around a rounded rect: additive strokes, far cheaper than shadowBlur on big shapes. */
  private neon(r: Rect, radius: number, col: RGB, strength: number) {
    if (strength <= 0.01) return;
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const [width, alpha] of [
      [14, 0.05],
      [8, 0.09],
      [4, 0.16]
    ]) {
      ctx.strokeStyle = rgba(col, alpha * strength);
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.roundRect(r.x, r.y, r.w, r.h, radius);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** A glowing dot, stretched along its direction of travel. */
  private orb(x: number, y: number, r: number, color: RGB, alpha = 1, vx = 0, vy = 0) {
    if (alpha <= 0) return;
    const ctx = this.ctx;
    const speed = Math.hypot(vx, vy);
    const stretch = 1 + Math.min(speed / 500, 1.3);
    this.glow.draw(ctx, x, y, r * 3.6, color, alpha * 0.85);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(Math.atan2(vy, vx));
    ctx.globalAlpha *= alpha;
    ctx.fillStyle = rgba(color, 1);
    ctx.beginPath();
    ctx.ellipse(0, 0, r * stretch, r / Math.sqrt(stretch), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = rgba(mix(color, C.white, 0.75), 1);
    ctx.beginPath();
    ctx.ellipse(0, 0, r * 0.45 * stretch, (r * 0.45) / Math.sqrt(stretch), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** Draws an orb whose position is a function of time, with a motion trail. */
  private movingOrb(at: (t: number) => { x: number; y: number; r: number; a: number } | null, t: number, color: RGB) {
    const now = at(t);
    if (!now) return;
    const before = at(t - 0.016);
    const vx = before ? (now.x - before.x) / 0.016 : 0;
    const vy = before ? (now.y - before.y) / 0.016 : 0;
    if (Math.hypot(vx, vy) > 30) {
      for (let k = 1; k <= 5; k++) {
        const p = at(t - k * 0.02);
        if (p) this.glow.draw(this.ctx, p.x, p.y, now.r * (2.6 - k * 0.3), color, now.a * (0.5 - k * 0.08));
      }
    }
    this.orb(now.x, now.y, now.r, color, now.a, vx, vy);
  }

  private packet(x: number, y: number, color: RGB, alpha = 1, dir = -1) {
    const ctx = this.ctx;
    this.glow.draw(ctx, x, y, 7, color, alpha * 0.8);
    ctx.fillStyle = rgba(mix(color, C.white, 0.5), alpha);
    ctx.fillRect(x - 1.3, y - 1.3, 2.6, 2.6);
    ctx.strokeStyle = rgba(color, alpha * 0.45);
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y - dir * 9);
    ctx.stroke();
  }

  private pipe(x: number, y0: number, y1: number, activity: number) {
    const ctx = this.ctx;
    ctx.strokeStyle = rgba(C.frame, 0.9);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - 3, y0);
    ctx.lineTo(x - 3, y1);
    ctx.moveTo(x + 3, y0);
    ctx.lineTo(x + 3, y1);
    ctx.stroke();
    if (activity > 0.01) {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = rgba(C.cyan, 0.35 * activity);
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(x, y0);
      ctx.lineTo(x, y1);
      ctx.stroke();
      ctx.strokeStyle = rgba(C.cyan, 0.8 * activity);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x - 3, y0);
      ctx.lineTo(x - 3, y1);
      ctx.moveTo(x + 3, y0);
      ctx.lineTo(x + 3, y1);
      ctx.stroke();
      ctx.restore();
    }
  }

  /** A block of memory cells that twinkle while being read. */
  private memoryGrid(r: Rect, activity: number, seed: number, ambient: number, pad = 5) {
    const ctx = this.ctx;
    const step = 5.5;
    const tick = Math.floor(ambient * 14);
    let n = 0;
    for (let y = r.y + pad; y < r.y + r.h - 2; y += step) {
      for (let x = r.x + pad; x < r.x + r.w - 2; x += step) {
        n++;
        const lit = activity > 0 && hash(seed, n, tick) < 0.25 + activity * 0.35;
        const a = lit ? 0.35 + activity * 0.65 : 0.55;
        ctx.fillStyle = lit ? rgba(C.packet, a) : rgba(C.memDot, a);
        ctx.fillRect(x, y, 1.8, 1.8);
      }
    }
  }

  private heatBox(
    r: Rect,
    heat: number,
    t: number,
    seed: number,
    dtSim: number,
    radius = 8,
    cells = true,
    cellAlpha = 0.8
  ) {
    const ctx = this.ctx;
    let dx = 0;
    let dy = 0;
    if (!this.reducedMotion && heat > 0.3) {
      const amp = Math.pow(heat, 1.5) * 1.8;
      dx = amp * noise(t * 38, seed);
      dy = amp * noise(t * 41, seed + 9);
    }
    const box = { x: r.x + dx, y: r.y + dy, w: r.w, h: r.h };
    const col = heatColor(heat);
    this.neon(box, radius, col, heat * 1.6);
    this.rect(
      box,
      radius,
      rgba(mix(C.panel, C.ember, heat * 0.12), 0.96),
      rgba(col, 0.55 + heat * 0.45),
      1.3 + heat * 1.4
    );
    if (cells) {
      const size = 6;
      const gap = 4;
      const cols = Math.floor((r.w - 12) / (size + gap));
      const rows = Math.floor((r.h - 26) / (size + gap));
      const ox = box.x + (r.w - (cols * (size + gap) - gap)) / 2;
      const oy = box.y + 20;
      const tick = Math.floor(t * 16);
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          const lit = hash(seed, j * cols + i, tick) < heat * 1.05;
          ctx.fillStyle = lit ? rgba(heatColor(clamp(heat, 0.45, 0.7)), cellAlpha) : rgba(C.cell, 1);
          ctx.fillRect(ox + i * (size + gap), oy + j * (size + gap), size, size);
        }
      }
    }
    if (heat > 0.45) {
      // Heat haze rising off the top
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = rgba(C.hot, (heat - 0.45) * 0.5);
      ctx.lineWidth = 1;
      for (let k = 0; k < 3; k++) {
        const hx = box.x + r.w * (0.25 + k * 0.25);
        ctx.beginPath();
        for (let s = 0; s <= 10; s++) {
          const yy = box.y - s * 2.6;
          const xx = hx + Math.sin(t * 9 + s * 0.8 + k * 2) * 2.2;
          if (s === 0) ctx.moveTo(xx, yy);
          else ctx.lineTo(xx, yy);
        }
        ctx.stroke();
      }
      ctx.restore();
    }
    if (dtSim > 0 && heat > 0.55 && !this.reducedMotion) {
      const expected = heat * heat * 34 * dtSim * (r.w / 90);
      let count = Math.floor(expected);
      if (Math.random() < expected - count) count++;
      for (let k = 0; k < count; k++) {
        this.sparks.burst(box.x + Math.random() * r.w, box.y, heatColor(0.6 + Math.random() * 0.4), 1, 140, {
          angle: -Math.PI / 2,
          spread: 1.4,
          gravity: 160,
          life: 0.55,
          size: 1.3
        });
      }
    }
    return box;
  }

  // ---------------------------------------------------------------------------
  // Overview

  private slot(s: number, k: number, n: number): Point {
    return { x: stationCx(s), y: s === ST_LM || n === 1 ? ROW_Y + 4 : ROW_Y + 6 + (k - (n - 1) / 2) * 9 };
  }

  private stationHeat(s: number, t: number) {
    let h = 0;
    for (const pass of this.tl.passes) {
      for (const run of pass.runs) {
        if (run.station !== s || !run.compute) continue;
        h = Math.max(h, run.compute.heat * envelope(t, run.compute.start, run.compute.end, 0.15, 0.4));
      }
    }
    return h;
  }

  private dropCurve(pass: Pass, k: number): [Point, Point, Point, Point] {
    const chip = this.chips[pass.tokens[k]];
    const from = { x: chip.cx, y: CHIP_Y + CHIP_H };
    const to = this.slot(ST_EMBED, k, pass.tokens.length);
    if (pass.kind === "prefill") return [from, { x: from.x, y: from.y + 70 }, { x: to.x, y: to.y - 110 }, to];
    return [from, { x: from.x - 40, y: 176 }, { x: to.x + 70, y: 150 }, to];
  }

  private overviewOrb(pass: Pass, k: number, t: number): { x: number; y: number; r: number; a: number } | null {
    const n = pass.tokens.length;
    const ds = pass.dropStart[k];
    if (t < ds) return null;
    if (t < ds + pass.dropDur) {
      const [p0, p1, p2, p3] = this.dropCurve(pass, k);
      const p = cubic(p0, p1, p2, p3, easeInOutCubic(prog(t, ds, pass.dropDur)));
      return { ...p, r: 2.6, a: 1 };
    }
    const embed = pass.runs[0];
    const inflate = embed.lookups[k].at + pass.pipeTime;
    const full = n === 1 ? 5.2 : 4.4;
    let r = t < inflate ? 2.6 : 2.6 + (full - 2.6) * easeOutBack(prog(t, inflate, 0.3));
    let pos = this.slot(ST_EMBED, k, n);
    let prevLeave = embed.leave;
    for (let ri = 1; ri < pass.runs.length; ri++) {
      const run = pass.runs[ri];
      const isLm = run.station === ST_LM;
      if (isLm && k !== n - 1) {
        const f = prog(t, pass.retire, 0.45);
        if (f >= 1) return null;
        return { ...pos, r: r * (1 - 0.6 * f), a: 1 - f };
      }
      const start = prevLeave + (isLm ? 0 : k * pass.stagger);
      if (t < start) return { ...pos, r, a: 1 };
      const to = this.slot(run.station, k, n);
      if (t < start + pass.travel) {
        const e = easeInOutCubic(prog(t, start, pass.travel));
        return { x: lerp(pos.x, to.x, e), y: lerp(pos.y, to.y, e), r, a: 1 };
      }
      pos = to;
      prevLeave = run.leave;
      if (t < run.leave) return { ...pos, r, a: 1 };
    }
    return null;
  }

  private drawOverview(t: number, dtSim: number, ambient: number) {
    const ctx = this.ctx;
    const tl = this.tl;

    // Memory strip
    this.rect(HBM, 10, rgba(hexToRgb("#050a18"), 0.95), rgba(C.frame, 1), 1);
    this.text("GPU MEMORY (HBM)", HBM.x + 12, HBM.y + 16, 10, C.label, 0.9);

    // Traffic per pipe, and packets in flight
    const weightsActivity = new Array(STATIONS.length).fill(0);
    const kvActivity = new Array(STATIONS.length).fill(0);
    const flying: Array<() => void> = [];
    const readFlash: Array<{ s: number; token: number; a: number }> = [];

    for (const pass of tl.passes) {
      const pt = pass.pipeTime;
      for (const run of pass.runs) {
        const s = run.station;
        run.packets.forEach((e, idx) => {
          const p = (t - e) / pt;
          if (p < 0 || p > 1) return;
          weightsActivity[s] = Math.min(1, weightsActivity[s] + 0.4);
          const x = weightsPipeX(s);
          const y = lerp(SEG_TOP, PIPE_TOP, p);
          const color = s === ST_EMBED ? TOKENS[run.lookups[Math.floor(idx / 3)].token].color : C.packet;
          flying.push(() => this.packet(x, y, color, 1, -1));
        });
        const linear = this.model === "linear";
        // A KV cache takes one entry per token; a linear-attention state is stored whole, once per pass.
        const lastWrite = run.kvWrites.length ? Math.max(...run.kvWrites.map((w) => w.leave)) : 0;
        for (const w of run.kvWrites) {
          const p = (t - w.leave) / pt;
          if (p < 0 || p > 1 || (s === ST_L1 && pass.interior) || (linear && w.leave !== lastWrite)) continue;
          kvActivity[s] = Math.min(1, kvActivity[s] + 0.5);
          const x = kvPipeX(s);
          const col = linear ? MODEL_COLORS.linear : TOKENS[w.token].color;
          flying.push(() => this.packet(x, lerp(PIPE_TOP, SEG_TOP, p), col, 1, 1));
        }
        if (linear && run.kvReads.length) {
          // Reading the state costs the same two packets no matter how long the text is.
          const first = run.kvReads[0].leave;
          for (const e of [first, first + 0.12 * pt]) {
            const p = (t - e) / pt;
            if (p < -0.2 || p > 1) continue;
            readFlash.push({ s, token: -1, a: p < 0 ? 1 + p * 5 : 1 - p });
            if (p < 0) continue;
            kvActivity[s] = Math.min(1, kvActivity[s] + 0.4);
            const x = kvPipeX(s);
            flying.push(() => this.packet(x, lerp(SEG_TOP, PIPE_TOP, p), MODEL_COLORS.linear, 1, -1));
          }
        }
        for (const r of linear ? [] : run.kvReads) {
          const p = (t - r.leave) / pt;
          if (p < -0.2 || p > 1) continue;
          if (p < 0) {
            readFlash.push({ s, token: r.token, a: 1 + p * 5 });
            continue;
          }
          readFlash.push({ s, token: r.token, a: 1 - p });
          kvActivity[s] = Math.min(1, kvActivity[s] + 0.4);
          const x = kvPipeX(s);
          flying.push(() => this.packet(x, lerp(SEG_TOP, PIPE_TOP, p), TOKENS[r.token].color, 1, -1));
        }
      }
    }

    // Memory blocks
    STATIONS.forEach((st, s) => {
      const wb = weightsBlock(s);
      this.rect(wb, 4, rgba(hexToRgb("#081229"), 1), rgba(C.frame, 0.9), 1);
      if (s === ST_EMBED) this.drawEmbeddingTable(wb, t, ambient);
      else this.memoryGrid(wb, weightsActivity[s], s * 13, ambient);
      if (hasKv(s)) {
        if (this.model === "linear") this.drawStateBlock(s, t, readFlash);
        else this.drawKvStack(s, t, readFlash);
      }
    });
    this.text("embeddings", stationCx(ST_EMBED), HBM.y + HBM.h + 14, 10, C.dim, 1, { font: "mono", align: "center" });
    this.text("weights", STATIONS[ST_L1].x, HBM.y + HBM.h + 14, 10, C.dim, 1, { font: "mono" });
    this.text(
      this.model === "linear" ? "state" : "KV cache",
      STATIONS[ST_L1].x + STATIONS[ST_L1].w + 6,
      HBM.y + HBM.h + 14,
      10,
      C.dim,
      1,
      {
        font: "mono",
        align: "right"
      }
    );
    this.text("weights", stationCx(ST_LM), HBM.y + HBM.h + 14, 10, C.dim, 1, { font: "mono", align: "center" });

    // Pipes
    STATIONS.forEach((_, s) => {
      this.pipe(weightsPipeX(s), PIPE_TOP, SEG_TOP, weightsActivity[s]);
      if (hasKv(s)) this.pipe(kvPipeX(s), PIPE_TOP, SEG_TOP, kvActivity[s]);
    });
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    flying.forEach((f) => f());
    ctx.restore();

    // Stations
    const current = this.currentPass(t);
    STATIONS.forEach((st, s) => {
      const heat = this.stationHeat(s, t);
      const r = { x: st.x, y: BOX_TOP, w: st.w, h: BOX_H };
      const memGlow = Math.max(weightsActivity[s], s === ST_EMBED ? 0 : kvActivity[s] * 0.6);
      if (s === ST_WARP) this.drawWarp(r, heat, t, dtSim, current);
      else {
        const box = this.heatBox(r, heat, t, s + 1, dtSim, 8, s !== ST_EMBED);
        if (memGlow > 0.05) {
          ctx.save();
          ctx.globalCompositeOperation = "lighter";
          this.rect(box, 8, null, rgba(C.cyan, memGlow * 0.5), 1);
          ctx.restore();
        }
      }
      const active = heat > 0.1 || memGlow > 0.1;
      this.text(st.label, stationCx(s), BOX_TOP + 14, 10, active ? C.text : C.label, 1, { align: "center" });
    });

    this.drawWaiting(t);
    this.drawLogits(t);

    // Orbs
    for (const pass of tl.passes) {
      pass.tokens.forEach((token, k) => {
        this.movingOrb((tt) => this.overviewOrb(pass, k, tt), t, TOKENS[token].color);
      });
    }
  }

  private drawEmbeddingTable(r: Rect, t: number, ambient: number) {
    const ctx = this.ctx;
    const rows = 15;
    const rowH = (r.h - 8) / rows;
    for (let j = 0; j < rows; j++) {
      for (let x = r.x + 5; x < r.x + r.w - 3; x += 5.5) {
        ctx.fillStyle = rgba(C.memDot, 0.55 + 0.2 * hash(j, x, Math.floor(ambient * 2)));
        ctx.fillRect(x, r.y + 5 + j * rowH, 1.8, 1.8);
      }
    }
    for (const pass of this.tl.passes) {
      for (const l of pass.runs[0].lookups) {
        const a = envelope(t, l.at - 0.05, l.at + 0.2, 0.05, 0.4);
        if (a <= 0) continue;
        const j = Math.min(rows - 1, Math.floor((TOKENS[l.token].id / VOCAB_SIZE) * rows));
        const y = r.y + 5 + j * rowH;
        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        ctx.fillStyle = rgba(TOKENS[l.token].color, a * 0.9);
        ctx.fillRect(r.x + 3, y - 1.2, r.w - 6, 4.2);
        this.glow.draw(ctx, r.x + r.w / 2, y + 1, 20, TOKENS[l.token].color, a * 0.6);
        ctx.restore();
      }
    }
  }

  /** Overview memory for linear attention: a fixed grid that gets rewritten, never extended. */
  private drawStateBlock(s: number, t: number, reads: Array<{ s: number; token: number; a: number }>) {
    const ctx = this.ctx;
    const b = kvBlock(s);
    this.rect(b, 4, rgba(hexToRgb("#081229"), 1), rgba(C.frame, 0.9), 1);
    const folds = this.stationFolds(s);
    const read = reads.filter((r) => r.s === s).reduce((m, r) => Math.max(m, r.a), 0);
    const stored = folds.filter((f) => f.at <= t).reduce((m, f) => Math.max(m, f.at), -10);
    const fresh = 1 - prog(t, stored, 0.4);
    const cols = 3;
    const rows = 9;
    const cw = (b.w - 8) / cols;
    const ch = (b.h - 8) / rows;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = this.stateCell(r * cols + c, folds, t);
        const x = b.x + 4 + c * cw;
        const y = b.y + 4 + r * ch;
        if (!cell) {
          ctx.fillStyle = rgba(C.cell, 1);
          ctx.fillRect(x + 1, y + 1, cw - 2, ch - 2);
          continue;
        }
        const col = mix(cell.color, C.white, Math.max(fresh, read * 0.5));
        ctx.fillStyle = rgba(col, cell.a);
        ctx.fillRect(x + 1, y + 1, cw - 2, ch - 2);
      }
    }
    const flash = Math.max(fresh, read);
    if (flash > 0) this.glow.draw(ctx, b.x + b.w / 2, b.y + b.h / 2, 30, MODEL_COLORS.linear, flash * 0.7);
  }

  private drawKvStack(s: number, t: number, reads: Array<{ s: number; token: number; a: number }>) {
    const ctx = this.ctx;
    const b = kvBlock(s);
    this.rect(b, 4, rgba(hexToRgb("#081229"), 1), rgba(C.frame, 0.9), 1);
    const rowH = 7;
    for (const row of this.kvRows[s]) {
      if (t < row.land) continue;
      const i = row.token;
      const y = b.y + b.h - 4 - (i + 1) * (rowH + 1);
      const fresh = 1 - prog(t, row.land, 0.35);
      const read = reads.filter((r) => r.s === s && r.token === i).reduce((m, r) => Math.max(m, r.a), 0);
      const col = mix(TOKENS[i].color, C.white, Math.max(fresh, read * 0.6));
      ctx.fillStyle = rgba(col, 0.75 + 0.25 * Math.max(fresh, read));
      ctx.fillRect(b.x + 4, y, b.w / 2 - 5, rowH);
      ctx.fillStyle = rgba(col, 0.5 + 0.25 * Math.max(fresh, read));
      ctx.fillRect(b.x + b.w / 2 + 1, y, b.w / 2 - 5, rowH);
      if (fresh > 0 || read > 0)
        this.glow.draw(ctx, b.x + b.w / 2, y + rowH / 2, 16, TOKENS[i].color, Math.max(fresh, read) * 0.8);
    }
  }

  private drawWarp(r: Rect, heat: number, t: number, dtSim: number, pass: Pass | null) {
    const ctx = this.ctx;
    this.rect(r, 8, rgba(C.panel, 0.9), rgba(heatColor(heat), 0.35 + heat * 0.3), 1);
    const run = pass?.runs.find((x) => x.station === ST_WARP);
    const inside = run && t >= run.arrive - 0.1 && t <= run.leave;
    const slabs = 5;
    const slabW = 8;
    const gap = (r.w - 12 - slabs * slabW) / (slabs - 1);
    for (let i = 0; i < slabs; i++) {
      const x = r.x + 6 + i * (slabW + gap);
      const lit = inside && Math.floor(t * 22) % slabs === i;
      const h = lit ? Math.max(heat, 0.35) : heat * 0.6;
      const col = heatColor(h);
      ctx.save();
      if (h > 0.1) {
        ctx.shadowColor = rgba(col, 0.9);
        ctx.shadowBlur = (lit ? 18 : 8) * h * this.px;
      }
      this.rect(
        { x, y: r.y + 22, w: slabW, h: r.h - 30 },
        2,
        rgba(mix(C.cell, col, h * 0.6), 1),
        rgba(col, 0.6 + h * 0.4),
        1
      );
      ctx.restore();
    }
    if (inside && heat > 0.5 && dtSim > 0 && !this.reducedMotion && Math.random() < heat * dtSim * 30) {
      this.sparks.burst(r.x + Math.random() * r.w, r.y, heatColor(0.8), 1, 140, {
        angle: -Math.PI / 2,
        spread: 1.4,
        gravity: 160,
        life: 0.5,
        size: 1.3
      });
    }
    if (inside) {
      // Speed lines: dozens of layers blurring past
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      for (let k = 0; k < 7; k++) {
        const y = r.y + 16 + hash(k, Math.floor(t * 12)) * (r.h - 20);
        const x = r.x - 30 + ((t * 400 + k * 53) % (r.w + 60));
        ctx.strokeStyle = rgba(C.cyan, 0.25);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 18, y);
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  private currentPass(t: number): Pass | null {
    let cur: Pass | null = null;
    for (const p of this.tl.passes) if (t >= p.dropStart[0] - 0.01) cur = p;
    return cur;
  }

  private drawWaiting(t: number) {
    const pass = this.tl.passes[1];
    if (!pass || t < pass.runs[1].arrive - 0.5 || t > pass.burst) return;
    for (const run of pass.runs) {
      if (run.station === ST_EMBED || run.station === ST_LM) continue;
      const a = envelope(t, run.arrive, run.leave - 0.1, 0.1, 0.15);
      if (a <= 0) continue;
      const blink = Math.floor(t * 3) % 2 === 0 ? 1 : 0.55;
      const x = stationCx(run.station);
      this.text("WAITING", x, BOX_TOP - 16, 10, C.cyan, a * blink, { align: "center", glow: 6 });
      this.text("ON MEMORY", x, BOX_TOP - 5, 10, C.cyan, a * blink, { align: "center", glow: 6 });
    }
  }

  private scoreTargets(pass: Pass) {
    const count = pass.kind === "prefill" ? 80 : 46;
    const out: Array<{ row: number; delay: number; c: Point; to: Point }> = [];
    const total = CANDIDATE_WEIGHTS.reduce((a, b) => a + b, 0) + 0.9;
    for (let j = 0; j < count; j++) {
      const h1 = hash(pass.index, j, 1);
      let acc = 0;
      let row = -1;
      for (let r = 0; r < CANDIDATE_WEIGHTS.length; r++) {
        acc += CANDIDATE_WEIGHTS[r] / total;
        if (h1 < acc) {
          row = r;
          break;
        }
      }
      const to =
        row >= 0
          ? { x: BAR_X + 4 + hash(pass.index, j, 2) * CANDIDATE_WEIGHTS[row] * (BAR_MAX - 6), y: LOGIT_ROW_Y(row) - 3 }
          : { x: LOGITS.x + 14 + hash(pass.index, j, 3) * (LOGITS.w - 28), y: 336 + hash(pass.index, j, 4) * 30 };
      out.push({
        row,
        delay: hash(pass.index, j, 5) * 0.25,
        c: { x: 790 + hash(pass.index, j, 6) * 50, y: ROW_Y + (hash(pass.index, j, 7) - 0.5) * 300 },
        to
      });
    }
    return out;
  }

  private drawLogits(t: number) {
    const ctx = this.ctx;
    this.rect(LOGITS, 8, rgba(C.panel, 0.92), rgba(C.frame, 1), 1);
    this.text("NEXT TOKEN", LOGITS.x + LOGITS.w / 2, LOGITS.y + 18, 10, C.label, 1, { align: "center" });

    let pass: Pass | null = null;
    for (const p of this.tl.passes) if (t >= p.burst - 0.01) pass = p;
    const live = pass && t < pass.land + 0.6;
    const fade = pass ? 1 - prog(t, pass.land, 0.5) : 0;
    const s = pass ? 1 / pass.speed : 1;

    for (let r = 0; r < CANDIDATE_WEIGHTS.length; r++) {
      const y = LOGIT_ROW_Y(r);
      if (!live) {
        this.text("····", LOGITS.x + 12, y, 12, C.dim, 0.5, { font: "mono" });
        continue;
      }
      const cands = CANDIDATES[pass.index];
      const grow = easeOutCubic(prog(t, pass.burst + 0.25 * s, 0.6 * s));
      const shrink = r === 0 ? 0 : easeInCubic(prog(t, pass.converge, 0.45 * s));
      const len = CANDIDATE_WEIGHTS[r] * BAR_MAX * grow * (1 - shrink);
      const winner = r === 0 && t >= pass.smash;
      const flash = r === 0 ? envelope(t, pass.smash, pass.smash + 0.1, 0.02, 0.4) : 0;
      const col = r === 0 ? TOKENS[pass.out].color : C.label;
      const textAlpha = grow * fade * (r === 0 ? 1 : 1 - shrink * 0.7);
      this.text(
        display(cands[r]),
        LOGITS.x + 10,
        y,
        13,
        winner ? mix(col, C.white, flash) : r === 0 ? col : C.text,
        textAlpha,
        { font: "mono", glow: winner ? 10 : 0 }
      );
      if (len > 0.5) {
        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        ctx.fillStyle = rgba(r === 0 ? col : C.cyan, (r === 0 ? 0.95 : 0.6) * fade);
        ctx.fillRect(BAR_X, y - 8, len, 8);
        ctx.restore();
      }
    }
    this.text("⋮", LOGITS.x + LOGITS.w / 2, 318, 14, C.dim, 0.9, { font: "mono", align: "center" });
    this.text("+ ~100K MORE", LOGITS.x + LOGITS.w / 2, 370, 9, C.dim, 0.9, { align: "center" });
    if (!pass || !live) return;

    // The spray: one vector becomes a score for every token.
    const targets = this.scoreTargets(pass);
    const lm = { x: STATIONS[ST_LM].x + STATIONS[ST_LM].w, y: ROW_Y };
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    const winAt = { x: 840, y: LOGIT_ROW_Y(0) - 4 };
    targets.forEach((d, j) => {
      const start = pass.burst + d.delay * s;
      const p = prog(t, start, 0.6 * s);
      if (p <= 0) return;
      const col = d.row === 0 ? TOKENS[pass.out].color : d.row > 0 ? C.cyan : C.dim;
      if (p < 1) {
        const pt = quad(lm, d.c, d.to, easeOutQuad(p));
        this.glow.draw(ctx, pt.x, pt.y, 5, col, 0.9);
        ctx.fillStyle = rgba(mix(col, C.white, 0.4), 1);
        ctx.fillRect(pt.x - 1, pt.y - 1, 2, 2);
        return;
      }
      if (d.row === -1 && t < pass.converge) {
        ctx.fillStyle = rgba(C.dim, 0.8);
        ctx.fillRect(d.to.x, d.to.y, 1.6, 1.6);
      }
      // Collapse: every other candidate is sucked into the winner.
      if (d.row !== 0 && t >= pass.converge && t < pass.smash) {
        const c = easeInCubic(prog(t, pass.converge + (j % 5) * 0.03 * s, pass.smash - pass.converge - 0.12 * s));
        const pt = { x: lerp(d.to.x, winAt.x, c), y: lerp(d.to.y, winAt.y, c) };
        this.glow.draw(ctx, pt.x, pt.y, 6, col, 0.9);
        ctx.fillStyle = rgba(C.white, 0.9);
        ctx.fillRect(pt.x - 1, pt.y - 1, 2, 2);
      }
    });
    if (t >= pass.burst && t < pass.burst + 0.3 * s) {
      const f = 1 - prog(t, pass.burst, 0.3 * s);
      this.glow.draw(ctx, lm.x, lm.y, 40 * f + 10, TOKENS[pass.tokens[pass.tokens.length - 1]].color, f);
    }
    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // Interior: layer 1, zoomed in

  private interiorOrbX(i: number, t: number) {
    const I = this.tl.passes[0].interior;
    let x = I_IN_X;
    x = lerp(x, I_ATTN_X, easeInOutCubic(prog(t, I.enter + i * 0.05, 0.9)));
    x = lerp(x, I_ROUTER_X, easeInOutCubic(prog(t, I.toRouter + i * 0.05, 0.6)));
    x = lerp(x, I_MERGE_X, easeInOutCubic(prog(t, I.toMerge + i * 0.06, 1.2)));
    x = lerp(x, I_EXIT_X, easeInCubic(prog(t, I.exit + i * 0.04, 0.6)));
    return x;
  }

  private drawInterior(t: number, dtSim: number, rect: Rect, panelAlpha: number, fill: number, content: number) {
    const ctx = this.ctx;
    const I = this.tl.passes[0].interior;
    ctx.save();
    ctx.globalAlpha = panelAlpha;
    this.neon(rect, 12, C.cyan, 1);
    this.rect(rect, 12, rgba(hexToRgb("#050a17"), 0.97 * fill), rgba(C.cyan, 0.7), 1.5);
    ctx.restore();
    if (content <= 0) return;

    ctx.save();
    ctx.beginPath();
    ctx.roundRect(rect.x, rect.y, rect.w, rect.h, 12);
    ctx.clip();
    ctx.globalAlpha = content;

    this.text("INSIDE LAYER 1", PANEL.x + 20, PANEL.y + 22, 13, C.text, 1, { glow: 6 });
    this.text("OF 90", PANEL.x + 176, PANEL.y + 22, 10, C.dim, 1);

    // Residual lanes: each token's running vector
    for (let i = 0; i < PROMPT_COUNT; i++) {
      ctx.strokeStyle = rgba(TOKENS[i].color, 0.1);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PANEL.x + 60, I_Y(i));
      ctx.lineTo(PANEL.x + PANEL.w, I_Y(i));
      ctx.stroke();
    }

    // Memory
    this.text("GPU", PANEL.x + 20, I_MEM_TOP + 22, 10, C.label, 1);
    this.text("MEMORY", PANEL.x + 20, I_MEM_TOP + 36, 10, C.label, 1);
    const attnPackets = I.attnPackets.map((e) => (t - e) / 0.35).filter((p) => p >= 0 && p <= 1);
    const moe = this.model === "moe";
    const linear = this.model === "linear";
    const expertPackets = I.expertPackets
      .map((e, k) => ({ p: (t - e) / (moe ? 0.22 : 0.35), e: k % (moe ? EXPERT_COUNT : FFN_PIPES.length) }))
      .filter((x) => x.p >= 0 && x.p <= 1);
    const kvMoving = linear
      ? prog(t, I.stateStore, I.kvTravel) > 0 && prog(t, I.stateStore, I.kvTravel) < 1
        ? 1
        : 0
      : I.kvLeave.map((l) => prog(t, l, I.kvTravel)).filter((p) => p > 0 && p < 1).length;

    // Token memory: a KV cache that grows by one entry per token, or a fixed-size state
    this.rect(I_KV, 4, rgba(hexToRgb("#081229"), 1), rgba(C.frame, 1), 1);
    this.text(linear ? "STATE" : "KV CACHE", I_KV.x + 6, I_KV.y + 11, 9, C.label, 1);
    if (linear) {
      const stored = I.stateStore + I.kvTravel;
      const folds = I.foldLaunch.map((l, i) => ({ token: i, at: stored }));
      const fresh = t >= stored ? 1 - prog(t, stored, 0.4) : 0;
      const cols = 8;
      const rows = 3;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const cell = this.stateCell(r * cols + c, folds, t);
          ctx.fillStyle = cell ? rgba(mix(cell.color, C.white, fresh), cell.a) : rgba(C.cell, 1);
          ctx.fillRect(I_KV.x + 6 + c * 12, I_KV.y + 16 + r * 12.5, 10, 10.5);
        }
      }
      if (fresh > 0) this.glow.draw(ctx, I_KV.x + I_KV.w / 2, I_KV.y + 34, 30, MODEL_COLORS.linear, fresh);
    } else {
      for (let i = 0; i < PROMPT_COUNT; i++) {
        const land = I.kvLeave[i] + I.kvTravel;
        if (t < land) continue;
        const fresh = 1 - prog(t, land, 0.4);
        const col = mix(TOKENS[i].color, C.white, fresh);
        ctx.fillStyle = rgba(col, 0.9);
        ctx.fillRect(kvColumnX(i), I_KV.y + 16, 7, 17);
        ctx.fillStyle = rgba(col, 0.6);
        ctx.fillRect(kvColumnX(i), I_KV.y + 35, 7, 17);
        if (fresh > 0) this.glow.draw(ctx, kvColumnX(i) + 3.5, I_KV.y + 32, 18, TOKENS[i].color, fresh);
      }
    }
    this.rect(I_ATTN_W, 4, rgba(hexToRgb("#081229"), 1), rgba(C.frame, 1), 1);
    this.text("WEIGHTS", I_ATTN_W.x + 6, I_ATTN_W.y + 11, 9, C.label, 1);
    this.memoryGrid(
      { ...I_ATTN_W, y: I_ATTN_W.y + 12, h: I_ATTN_W.h - 12 },
      Math.min(1, attnPackets.length / 3),
      101,
      t
    );
    if (moe) {
      this.text("EXPERT WEIGHTS", expertX(0) + 4, I_MEM_TOP + 11, 9, C.label, 1);
      for (let e = 0; e < EXPERT_COUNT; e++) {
        const b = { x: expertX(e), y: I_MEM_TOP + 14, w: EXPERT_W, h: I_MEM_BOTTOM - I_MEM_TOP - 14 };
        this.rect(b, 3, rgba(hexToRgb("#081229"), 1), rgba(C.frame, 1), 1);
        const act = Math.min(1, expertPackets.filter((x) => x.e === e).length / 1.5);
        this.memoryGrid(b, act, 200 + e, t, 4);
      }
    } else {
      const b = { x: FFN.x, y: I_MEM_TOP, w: FFN.w, h: I_MEM_BOTTOM - I_MEM_TOP };
      this.rect(b, 4, rgba(hexToRgb("#081229"), 1), rgba(C.frame, 1), 1);
      this.text("FEED-FORWARD WEIGHTS", b.x + 6, b.y + 11, 9, C.label, 1);
      this.memoryGrid({ ...b, y: b.y + 12, h: b.h - 12 }, Math.min(1, expertPackets.length / 3), 250, t);
    }

    // Pipes and weight packets
    this.pipe(I_KV_PIPE_X, ATTN.y + ATTN.h, I_MEM_TOP, Math.min(1, kvMoving / 2));
    this.pipe(I_W_PIPE_X, ATTN.y + ATTN.h, I_MEM_TOP, Math.min(1, attnPackets.length / 3));
    if (moe) {
      for (let e = 0; e < EXPERT_COUNT; e++)
        this.pipe(
          expertCx(e),
          EXPERT_TOP + EXPERT_H,
          I_MEM_TOP + 14,
          Math.min(1, expertPackets.filter((x) => x.e === e).length / 1.5)
        );
    } else {
      FFN_PIPES.forEach((x, k) =>
        this.pipe(x, FFN.y + FFN.h, I_MEM_TOP, Math.min(1, expertPackets.filter((p) => p.e === k).length / 2))
      );
    }
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const p of attnPackets) this.packet(I_W_PIPE_X, lerp(I_MEM_TOP, ATTN.y + ATTN.h, p), C.packet);
    for (const x of expertPackets) {
      if (moe) this.packet(expertCx(x.e), lerp(I_MEM_TOP + 14, EXPERT_TOP + EXPERT_H, x.p), C.packet);
      else this.packet(FFN_PIPES[x.e], lerp(I_MEM_TOP, FFN.y + FFN.h, x.p), C.packet);
    }
    ctx.restore();

    // Attention box
    const attnHeat = Math.max(
      envelope(t, I.attnHeat[0], I.attnHeat[1], 0.2, 0.4),
      0.3 * envelope(t, I.mixHeat[0], I.mixHeat[1], 0.2, 0.4)
    );
    this.heatBox(ATTN, attnHeat, t, 77, dtSim * content, 10, true, 0.45);
    this.text(
      linear ? "LINEAR ATTENTION" : "ATTENTION",
      ATTN.x + 10,
      ATTN.y + 15,
      11,
      attnHeat > 0.1 ? C.text : C.label,
      1
    );

    const merge = (i: number) => I.expertOut[i] + I.returnTravel;
    if (moe) this.drawExperts(t, dtSim * content, I);
    else {
      const heat = envelope(t, I.dispatch[0] + 0.2, merge(PROMPT_COUNT - 1) - 0.3, 0.25, 0.4);
      this.heatBox(FFN, heat, t, 88, dtSim * content, 10, true, 0.45);
      this.text("FEED-FORWARD", FFN.x + 10, FFN.y + 15, 11, heat > 0.1 ? C.text : C.label, 1);
    }

    // Merge points on the residual lanes
    for (let i = 0; i < PROMPT_COUNT; i++) {
      const hit = envelope(t, merge(i), merge(i) + 0.05, 0.02, 0.4);
      ctx.strokeStyle = rgba(mix(C.dim, TOKENS[i].color, hit), 0.5 + hit * 0.5);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(I_MERGE_X, I_Y(i), 7, 0, Math.PI * 2);
      ctx.moveTo(I_MERGE_X - 4, I_Y(i));
      ctx.lineTo(I_MERGE_X + 4, I_Y(i));
      ctx.moveTo(I_MERGE_X, I_Y(i) - 4);
      ctx.lineTo(I_MERGE_X, I_Y(i) + 4);
      ctx.stroke();
    }

    if (linear) this.drawStateMixing(t, I);
    else this.drawAttentionArcs(t, I);

    // Memory writes: one KV entry per token, or the whole state once
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    if (linear) {
      const p = prog(t, I.stateStore, I.kvTravel);
      if (p > 0 && p < 1) {
        const pt = alongPolyline(this.statePath, easeInOutCubic(p));
        this.glow.draw(ctx, pt.x, pt.y, 14, MODEL_COLORS.linear, 1);
        ctx.fillStyle = rgba(mix(MODEL_COLORS.linear, C.white, 0.4), 1);
        ctx.fillRect(pt.x - 4, pt.y - 4, 8, 8);
      }
    } else {
      for (let i = 0; i < PROMPT_COUNT; i++) {
        const p = prog(t, I.kvLeave[i], I.kvTravel);
        if (p <= 0 || p >= 1) continue;
        const pt = alongPolyline(this.kvPaths[i], easeInOutCubic(p));
        this.glow.draw(ctx, pt.x, pt.y, 10, TOKENS[i].color, 0.9);
        ctx.fillStyle = rgba(mix(TOKENS[i].color, C.white, 0.4), 1);
        ctx.fillRect(pt.x - 4, pt.y - 2, 3.4, 3.4);
        ctx.fillRect(pt.x + 0.6, pt.y - 2, 3.4, 3.4);
      }
    }
    ctx.restore();

    if (moe) this.drawExpertTraffic(t, I);
    else this.drawFeedForwardFans(t, I);

    // Token orbs on their lanes
    for (let i = 0; i < PROMPT_COUNT; i++) {
      const x = this.interiorOrbX(i, t);
      const y = I_Y(i);
      const mixHit = linear
        ? I.foldLaunch[i] + I.foldTravel + 0.05 + I.readTravel
        : i > 0
          ? I.mixLaunch[i] + I.mixTravel
          : -1;
      const hits = [mixHit, merge(i)];
      if (!moe && t >= I.dispatch[i] && t < merge(i)) continue;
      let bump = 0;
      let richness = 0;
      for (const h of hits) {
        if (h < 0 || t < h) continue;
        richness++;
        const p = prog(t, h, 0.45);
        bump = Math.max(bump, (1 - p) * (1 - p));
      }
      const prevX = this.interiorOrbX(i, t - 0.016);
      const r = 7.5 * (1 + 0.55 * bump) + richness * 0.8;
      if (Math.abs(x - prevX) > 0.5) {
        for (let k = 1; k <= 5; k++)
          this.glow.draw(
            ctx,
            this.interiorOrbX(i, t - k * 0.02),
            y,
            r * (2.4 - k * 0.3),
            TOKENS[i].color,
            0.45 - k * 0.07
          );
      }
      this.orb(x, y, r, TOKENS[i].color, 1, (x - prevX) / 0.016, 0);
      const labelA = 1 - prog(t, I.enter, 0.5);
      this.text(display(TOKENS[i].text), I_IN_X - 16, y + 4, 12, TOKENS[i].color, labelA, {
        font: "mono",
        align: "right"
      });
    }

    this.drawEvents(t, "interior");
    ctx.restore();
  }

  private drawExperts(t: number, dtSim: number, I: Interior) {
    const routing = I.dispatch
      .map((d) => envelope(t, d - 0.05, d + 0.1, 0.05, 0.25))
      .reduce((a, b) => Math.max(a, b), 0);
    this.rect(ROUTER, 6, rgba(C.panel, 1), rgba(mix(C.idle, C.cyan, routing), 0.7 + routing * 0.3), 1.2 + routing);
    this.text("ROUTER", ROUTER.x + ROUTER.w / 2, ROUTER.y - 5, 9, routing > 0.1 ? C.text : C.label, 1, {
      align: "center"
    });
    this.text("EXPERTS", expertX(0) - 8, EXPERT_CY + 4, 9, C.label, 1, { align: "right" });
    const inside: number[] = new Array(EXPERT_COUNT).fill(0);
    ROUTES.forEach(([a, b], i) => {
      for (const e of [a, b])
        inside[e] += envelope(t, I.dispatch[i] + I.dispatchTravel - 0.1, I.expertOut[i], 0.15, 0.3);
    });
    for (let e = 0; e < EXPERT_COUNT; e++) {
      const heat = Math.min(1, inside[e] * 0.5);
      this.heatBox({ x: expertX(e), y: EXPERT_TOP, w: EXPERT_W, h: EXPERT_H }, heat, t, 300 + e, dtSim, 5, false);
      this.text(`E${e + 1}`, expertCx(e), EXPERT_TOP + EXPERT_H - 5, 8, heat > 0.1 ? C.text : C.dim, 1, {
        align: "center"
      });
    }
  }

  /** Each token sends two copies to its experts; the answers fly back and merge into it. */
  private drawExpertTraffic(t: number, I: Interior) {
    ROUTES.forEach(([a, b], i) => {
      const y = I_Y(i);
      [a, b].forEach((e, q) => {
        const dStart = I.dispatch[i];
        const inAt = dStart + I.dispatchTravel;
        const out = I.expertOut[i];
        const back = out + I.returnTravel;
        const at = (tt: number) => {
          if (tt < dStart || tt > back) return null;
          if (tt < inAt) {
            const p = easeInOutCubic(prog(tt, dStart, I.dispatchTravel));
            const pt = cubic(
              { x: ROUTER.x + ROUTER.w, y },
              { x: 530, y },
              { x: expertCx(e), y: 360 },
              { x: expertCx(e), y: EXPERT_CY },
              p
            );
            return { ...pt, r: 3.8, a: 1 };
          }
          if (tt < out) {
            const ang = tt * 5 + i * 1.7 + q * Math.PI;
            return { x: expertCx(e) + Math.cos(ang) * 6, y: EXPERT_CY + Math.sin(ang) * 5, r: 3.8, a: 1 };
          }
          const p = easeInCubic(prog(tt, out, I.returnTravel));
          const pt = cubic(
            { x: expertCx(e), y: EXPERT_CY },
            { x: expertCx(e), y: 350 },
            { x: I_MERGE_X - 70, y: y + 20 },
            { x: I_MERGE_X, y },
            p
          );
          return { ...pt, r: 3.8, a: 1 };
        };
        this.movingOrb(at, t, TOKENS[i].color);
      });
    });
  }

  /** Dense feed-forward: every token fans out several times wider, then gets squeezed back into one. */
  private drawFeedForwardFans(t: number, I: Interior) {
    const spread = (p: number) =>
      p < 0.22 ? easeOutCubic(p / 0.22) : p > 0.72 ? 1 - easeInCubic((p - 0.72) / 0.28) : 1;
    for (let i = 0; i < PROMPT_COUNT; i++) {
      const start = I.dispatch[i];
      const end = I.expertOut[i] + I.returnTravel;
      const y = I_Y(i);
      for (let k = 0; k < 6; k++) {
        const offset = ((k - 2.5) / 2.5) * 13;
        const at = (tt: number) => {
          if (tt < start || tt >= end) return null;
          const p = prog(tt, start, end - start);
          const x = lerp(I_ROUTER_X, I_MERGE_X, easeInOutCubic(p));
          const w = Math.sin(tt * 9 + k * 1.3 + i) * 1.2 * spread(p);
          return { x, y: y + offset * spread(p) + w, r: 2.6, a: 1 };
        };
        this.movingOrb(at, t, TOKENS[i].color);
      }
    }
  }

  private drawAttentionArcs(t: number, I: Interior) {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (let i = 1; i < PROMPT_COUNT; i++) {
      const launch = I.mixLaunch[i];
      const arrive = launch + I.mixTravel;
      const show = envelope(t, launch - 0.25, I.toRouter, 0.2, 0.5);
      if (show <= 0) continue;
      const active = envelope(t, launch - 0.25, arrive + 0.15, 0.2, 0.5);
      for (let j = 0; j < i; j++) {
        const w = attentionWeight(i, j);
        const p0 = { x: I_ATTN_X, y: I_Y(j) };
        const p2 = { x: I_ATTN_X, y: I_Y(i) };
        const p1 = { x: I_ATTN_X - (30 + 24 * (i - j)), y: (p0.y + p2.y) / 2 };
        ctx.strokeStyle = rgba(TOKENS[j].color, show * (0.12 + active * (0.25 + 0.65 * w)));
        ctx.lineWidth = 0.8 + (0.5 + active) * 2 * w;
        ctx.beginPath();
        ctx.moveTo(p0.x, p0.y);
        ctx.quadraticCurveTo(p1.x, p1.y, p2.x, p2.y);
        ctx.stroke();
        const p = prog(t, launch, I.mixTravel);
        if (p > 0 && p < 1) {
          const pt = quad(p0, p1, p2, easeInCubic(p));
          const prev = quad(p0, p1, p2, easeInCubic(Math.max(0, p - 0.05)));
          this.orb(pt.x, pt.y, 1.8 + 3 * w, TOKENS[j].color, 1, (pt.x - prev.x) * 20, (pt.y - prev.y) * 20);
        }
      }
      // The query ping: a token about to look back
      const ping = prog(t, launch - 0.3, 0.45);
      if (ping > 0 && ping < 1) {
        ctx.strokeStyle = rgba(TOKENS[i].color, 1 - ping);
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(I_ATTN_X, I_Y(i), 9 + ping * 18, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /** Linear attention: tokens take turns smashing into one fixed-size state, then read the blend back. */
  private drawStateMixing(t: number, I: Interior) {
    const ctx = this.ctx;
    const g = STATE_GRID;
    const center = { x: g.x + g.w / 2, y: g.y + g.h / 2 };
    const folds = I.foldLaunch.map((l, i) => ({ token: i, at: l + I.foldTravel }));
    const cell = g.w / STATE_CELLS;
    this.text("STATE", center.x, g.y - 7, 9, C.label, 1, { align: "center" });
    const latest = folds.filter((f) => f.at <= t).reduce((m, f) => Math.max(m, f.at), -10);
    const fresh = 1 - prog(t, latest, 0.3);
    for (let r = 0; r < STATE_CELLS; r++) {
      for (let c = 0; c < STATE_CELLS; c++) {
        const v = this.stateCell(r * STATE_CELLS + c, folds, t);
        ctx.fillStyle = v ? rgba(mix(v.color, C.white, fresh * 0.4), v.a) : rgba(C.cell, 1);
        ctx.fillRect(g.x + c * cell + 1, g.y + r * cell + 1, cell - 2, cell - 2);
      }
    }
    ctx.strokeStyle = rgba(MODEL_COLORS.linear, 0.5 + fresh * 0.5);
    ctx.lineWidth = 1.2;
    ctx.strokeRect(g.x - 2, g.y - 2, g.w + 4, g.h + 4);
    if (fresh > 0) this.glow.draw(ctx, center.x, center.y, 50, MODEL_COLORS.linear, fresh * 0.5);

    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    const readCol = mix(MODEL_COLORS.linear, C.white, 0.3);
    I.foldLaunch.forEach((launch, i) => {
      const orb = { x: I_ATTN_X, y: I_Y(i) };
      const fold = prog(t, launch, I.foldTravel);
      if (fold > 0 && fold < 1) {
        const e = easeInCubic(fold);
        const prev = easeInCubic(Math.max(0, fold - 0.05));
        this.orb(
          lerp(orb.x, center.x, e),
          lerp(orb.y, center.y, e),
          3.6,
          TOKENS[i].color,
          1,
          (center.x - orb.x) * (e - prev) * 20,
          (center.y - orb.y) * (e - prev) * 20
        );
      }
      const read = prog(t, launch + I.foldTravel + 0.05, I.readTravel);
      if (read > 0 && read < 1) {
        const e = easeInCubic(read);
        const prev = easeInCubic(Math.max(0, read - 0.05));
        this.orb(
          lerp(center.x, orb.x, e),
          lerp(center.y, orb.y, e),
          3.2,
          readCol,
          1,
          (orb.x - center.x) * (e - prev) * 20,
          (orb.y - center.y) * (e - prev) * 20
        );
      }
    });
    ctx.restore();
  }

  // ---------------------------------------------------------------------------
  // Screen-space layers

  private drawEvents(t: number, space: Space) {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const e of this.events) {
      if (e.space !== space || !e.size) continue;
      const dur = e.big ? 0.7 : 0.45;
      const p = (t - e.t) / dur;
      if (p < 0 || p > 1) continue;
      const k = 1 - p;
      this.glow.draw(ctx, e.x, e.y, (e.big ? 70 : 26) * e.size * (0.4 + k * 0.6), mix(e.color, C.white, 0.5), k * k);
      ctx.strokeStyle = rgba(mix(e.color, C.white, 0.3), k);
      ctx.lineWidth = (e.big ? 4 : 2.2) * k + 0.4;
      ctx.beginPath();
      ctx.arc(e.x, e.y, 6 + easeOutCubic(p) * (e.big ? 90 : 26) * e.size, 0, Math.PI * 2);
      ctx.stroke();
      if (e.big) {
        ctx.strokeStyle = rgba(e.color, k * 0.6);
        ctx.lineWidth = 1.5 * k;
        ctx.beginPath();
        ctx.arc(e.x, e.y, 4 + easeOutCubic(clamp(p * 1.6)) * 55 * e.size, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  private drawContext(t: number) {
    const ctx = this.ctx;
    const tl = this.tl;
    this.rect(CONTEXT, 10, rgba(C.panel, 0.96), rgba(C.frame, 1), 1);
    this.text("CONTEXT", CONTEXT.x + 12, CONTEXT.y + 13, 9, C.label, 1);

    let count = 0;
    if (t >= tl.splitStart) count = PROMPT_COUNT;
    for (const p of tl.passes) if (t >= p.land) count = p.out + 1;
    if (count)
      this.text(`${count} TOKENS`, CONTEXT.x + CONTEXT.w - 12, CONTEXT.y + 13, 9, C.label, 1, { align: "right" });

    const baseline = CHIP_Y + 21;
    if (t < tl.tokenizeStart) {
      const typed = PROMPT.slice(0, Math.max(0, Math.floor((t - tl.typeStart) * 14)));
      this.text(typed, 40, baseline, 20, C.text, 1, { font: "mono" });
      if (Math.floor(t * 2.5) % 2 === 0 || (t > tl.typeStart && t < tl.typeEnd)) {
        ctx.fillStyle = rgba(C.cyan, 0.9);
        ctx.fillRect(40 + typed.length * this.cw + 2, CHIP_Y + 4, 10, 22);
      }
      return;
    }

    // Tokenizer scan beam
    const beam = prog(t, tl.tokenizeStart, 0.7);
    const beamX = lerp(30, 40 + PROMPT.length * this.cw + 10, easeInOutCubic(beam));

    for (let i = 0; i < TOKENS.length; i++) {
      const tok = TOKENS[i];
      const chip = this.chips[i];
      let rect: Rect;
      let e = 1;
      let squash = 1;
      if (i < PROMPT_COUNT) {
        e = easeOutBack(prog(t, tl.splitStart + i * 0.08, 0.45));
        const span = this.spans[i];
        rect = { x: lerp(span.x, chip.x, e), y: CHIP_Y, w: lerp(span.w, chip.w, clamp(e)), h: CHIP_H };
      } else {
        const pass = tl.passes.find((p) => p.out === i);
        if (!pass || t < pass.land) continue;
        const p = prog(t, pass.land, 0.35);
        squash = 1 + 0.35 * Math.sin(p * Math.PI) * (1 - p);
        rect = { x: chip.x, y: CHIP_Y, w: chip.w, h: CHIP_H };
      }
      const cx = rect.x + rect.w / 2;
      const border = clamp(e);
      const source = tl.passes.find(
        (p) =>
          p.tokens.includes(i) &&
          t >= p.dropStart[p.tokens.indexOf(i)] - 0.1 &&
          t < p.dropStart[p.tokens.indexOf(i)] + 0.4
      );
      const ping = source
        ? envelope(
            t,
            source.dropStart[source.tokens.indexOf(i)] - 0.1,
            source.dropStart[source.tokens.indexOf(i)] + 0.05,
            0.08,
            0.3
          )
        : 0;
      ctx.save();
      ctx.translate(cx, CHIP_Y + CHIP_H / 2);
      ctx.scale(1 / squash, squash);
      ctx.translate(-cx, -(CHIP_Y + CHIP_H / 2));
      if (border > 0) {
        if (ping > 0) {
          ctx.shadowColor = rgba(tok.color, 1);
          ctx.shadowBlur = 16 * ping * this.px;
        }
        this.rect(
          rect,
          6,
          rgba(mix(C.panel, tok.color, 0.16 + ping * 0.3), border),
          rgba(tok.color, border * (0.75 + ping * 0.25)),
          1.3
        );
        ctx.shadowBlur = 0;
      }
      const scanned = i < PROMPT_COUNT ? beamX > this.spans[i].x + 7 : true;
      const textCol = scanned ? mix(C.text, tok.color, 0.55) : C.text;
      if (tok.text.startsWith(" ") && border > 0) {
        const lx = cx - (tok.text.length * this.cw) / 2;
        this.text("·", lx, baseline, 20, C.dim, border, { font: "mono" });
        this.text(tok.text.slice(1), lx + this.cw, baseline, 20, textCol, 1, { font: "mono" });
      } else {
        this.text(
          tok.text.trimStart(),
          cx - (tok.text.trimStart().length * this.cw) / 2 + (tok.text.startsWith(" ") ? this.cw / 2 : 0),
          baseline,
          20,
          textCol,
          1,
          { font: "mono" }
        );
      }
      ctx.restore();

      // Token ID, rolling like a slot machine before it settles
      const idStart = i < PROMPT_COUNT ? tl.idStart + i * 0.07 : tl.passes.find((p) => p.out === i).land;
      if (t >= idStart) {
        const id = String(tok.id);
        let shown = "";
        for (let d = 0; d < id.length; d++)
          shown += t > idStart + 0.2 + d * 0.08 ? id[d] : String(Math.floor(hash(i, d, Math.floor(t * 30)) * 10));
        this.text(shown, cx, CHIP_Y + CHIP_H + 13, 9, tok.color, 0.85, { align: "center" });
      }
    }

    // Cut marks where the tokenizer splits words
    if (t < tl.splitStart + 0.3) {
      const fade = 1 - prog(t, tl.splitStart, 0.3);
      for (let i = 1; i < PROMPT_COUNT; i++) {
        const x = this.spans[i].x + 7;
        if (beamX < x) continue;
        ctx.fillStyle = rgba(C.white, 0.85 * fade);
        ctx.fillRect(x - 0.75, CHIP_Y + 2, 1.5, CHIP_H - 4);
      }
    }
    if (beam > 0 && beam < 1) {
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      const grad = ctx.createLinearGradient(beamX - 40, 0, beamX, 0);
      grad.addColorStop(0, rgba(C.cyan, 0));
      grad.addColorStop(1, rgba(C.cyan, 0.25));
      ctx.fillStyle = grad;
      ctx.fillRect(beamX - 40, CHIP_Y - 4, 40, CHIP_H + 8);
      ctx.fillStyle = rgba(C.white, 0.95);
      ctx.fillRect(beamX - 1, CHIP_Y - 6, 2, CHIP_H + 12);
      this.glow.draw(ctx, beamX, CHIP_Y + CHIP_H / 2, 26, C.cyan, 0.8);
      ctx.restore();
    }
  }

  private drawFlyingChip(t: number) {
    const ctx = this.ctx;
    for (const pass of this.tl.passes) {
      const s = 1 / pass.speed;
      if (t < pass.pop || t >= pass.land) continue;
      const tok = TOKENS[pass.out];
      const chip = this.chips[pass.out];
      const from = { x: 840, y: LOGIT_ROW_Y(0) - 4 };
      const to = { x: chip.cx, y: CHIP_Y + CHIP_H / 2 };
      const flyStart = pass.pop + 0.25 * s;
      const grow = easeOutBack(prog(t, pass.pop, 0.25 * s));
      const f = easeInOutCubic(prog(t, flyStart, pass.land - flyStart));
      const pt = cubic(from, { x: from.x - 30, y: 40 }, { x: to.x + 80, y: 8 }, to, f);
      const scale = lerp(0.2 + grow * 1.2, 1, f);
      ctx.save();
      ctx.translate(pt.x, pt.y);
      ctx.scale(scale, scale);
      ctx.shadowColor = rgba(tok.color, 1);
      ctx.shadowBlur = 20 * this.px;
      this.rect(
        { x: -chip.w / 2, y: -CHIP_H / 2, w: chip.w, h: CHIP_H },
        6,
        rgba(mix(C.panel, tok.color, 0.35), 1),
        rgba(tok.color, 1),
        1.6
      );
      ctx.shadowBlur = 0;
      const label = tok.text.trimStart();
      const lx = -(label.length * this.cw) / 2;
      const ab = 1 - prog(t, pass.pop, 0.4 * s);
      if (ab > 0) {
        // Chromatic split right after the smash
        ctx.globalCompositeOperation = "lighter";
        this.text(label, lx - 3 * ab, 7, 20, C.red, ab, { font: "mono" });
        this.text(label, lx + 3 * ab, 7, 20, C.cyan, ab, { font: "mono" });
        ctx.globalCompositeOperation = "source-over";
      }
      this.text(label, lx, 7, 20, C.white, 1, { font: "mono" });
      ctx.restore();
      if (f > 0.02) this.glow.draw(ctx, pt.x, pt.y, 30, tok.color, 0.6);
    }
  }

  private drawBanners(t: number) {
    this.banner(t, this.tl.prefillBanner, "PREFILL", "the whole prompt, all at once", C.cyan);
    this.banner(t, this.tl.decodeBanner, "DECODE", "one token at a time", C.gold);
  }

  private banner(t: number, t0: number, title: string, sub: string, color: RGB) {
    const local = t - t0;
    if (local < 0 || local > 1.6) return;
    const ctx = this.ctx;
    const inP = prog(local, 0, 0.3);
    const outP = prog(local, 1.2, 0.4);
    const alpha = clamp(inP * 3) * (1 - outP);
    const scale = lerp(2.4, 1, easeOutBack(inP)) * (1 + outP * 0.15);
    const cy = H / 2 + 8;
    ctx.save();
    ctx.globalAlpha = alpha;
    const bandH = 76 * easeOutCubic(clamp(inP * 1.5));
    ctx.fillStyle = "rgba(2,4,12,0.82)";
    ctx.fillRect(0, cy - bandH / 2, W, bandH);
    const sweep = easeOutCubic(prog(local, 0, 0.45));
    ctx.fillStyle = rgba(color, 0.9);
    ctx.fillRect(W * (1 - sweep), cy - bandH / 2, W * sweep, 2);
    ctx.fillRect(0, cy + bandH / 2 - 2, W * sweep, 2);
    ctx.translate(W / 2, cy);
    ctx.scale(scale, scale);
    this.text(title, 0, 8, 38, color, 1, { align: "center", glow: 18 });
    ctx.restore();
    this.text(sub.toUpperCase(), W / 2, cy + 30, 10, C.text, alpha * clamp((local - 0.25) * 4), { align: "center" });
  }

  private drawFlash(t: number) {
    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (const pass of this.tl.passes) {
      const p = prog(t, pass.smash, 0.45 / pass.speed);
      if (t < pass.smash || p >= 1) continue;
      const k = (1 - p) * (1 - p) * (pass.kind === "prefill" ? 1 : 0.6);
      const burst = ctx.createRadialGradient(840, LOGIT_ROW_Y(0), 0, 840, LOGIT_ROW_Y(0), 520);
      burst.addColorStop(0, rgba(C.white, 0.55 * k));
      burst.addColorStop(0.25, rgba(TOKENS[pass.out].color, 0.18 * k));
      burst.addColorStop(1, rgba(TOKENS[pass.out].color, 0));
      ctx.fillStyle = burst;
      ctx.fillRect(0, 0, W, H);
    }
    for (const [b, col] of [
      [this.tl.prefillBanner, C.cyan],
      [this.tl.decodeBanner, C.gold]
    ] as Array<[number, RGB]>) {
      const p = prog(t, b + 0.1, 0.3);
      if (t < b + 0.1 || p >= 1) continue;
      ctx.fillStyle = rgba(col, 0.12 * (1 - p));
      ctx.fillRect(0, 0, W, H);
    }
    ctx.restore();
  }

  /** A quick sweep in the new model's color when the architecture is switched. */
  private drawSwitch(ambient: number) {
    const ctx = this.ctx;
    const p = (ambient - this.switchedAt) / 0.45;
    if (p >= 0 && p < 1) {
      const col = MODEL_COLORS[this.model];
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.fillStyle = rgba(col, 0.1 * (1 - p));
      ctx.fillRect(0, 37, W, H - 37);
      const y = 37 + easeOutCubic(p) * (H - 37);
      const grad = ctx.createLinearGradient(0, y - 30, 0, y);
      grad.addColorStop(0, rgba(col, 0));
      grad.addColorStop(1, rgba(col, 0.35 * (1 - p)));
      ctx.fillStyle = grad;
      ctx.fillRect(0, y - 30, W, 30);
      ctx.restore();
    }
    this.uiSparks.draw(ctx, this.glow);
  }

  private drawEndCard(t: number) {
    const tl = this.tl;
    const a = easeOutCubic(prog(t, tl.endCard, 0.6));
    if (a <= 0) return;
    const ctx = this.ctx;
    const card: Rect = { x: 250, y: 178, w: 460, h: 176 };
    ctx.save();
    ctx.globalAlpha = a;
    ctx.translate(0, (1 - a) * 16);
    ctx.shadowColor = rgba(C.cyan, 0.6);
    ctx.shadowBlur = 24 * this.px;
    this.rect(card, 12, "rgba(4,8,20,0.94)", rgba(C.cyan, 0.8), 1.5);
    ctx.shadowBlur = 0;
    this.text("WHERE THE TIME GOES", W / 2, card.y + 30, 13, C.text, 1, { align: "center", glow: 6 });

    const row = (y: number, name: string, nameCol: RGB, verdict: string, icon: "heat" | "pipe", delay: number) => {
      const ra = easeOutCubic(prog(t, tl.endCard + delay, 0.4));
      ctx.globalAlpha = a * ra;
      this.text(name, card.x + 30, y + 6, 16, nameCol, 1, { glow: 8 });
      if (icon === "heat") {
        const h = 0.75 + 0.25 * Math.sin(t * 8);
        this.heatBox({ x: card.x + 166, y: y - 16, w: 48, h: 32 }, h, t, 999, 0, 5, false);
        for (let k = 0; k < 6; k++)
          this.glow.draw(ctx, card.x + 172 + k * 7, y + 2 + Math.sin(t * 20 + k) * 3, 4, C.hot, 0.6);
      } else {
        this.pipe(card.x + 190, y - 16, y + 16, 1);
        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        for (let k = 0; k < 4; k++) this.packet(card.x + 190, y + 16 - ((t * 60 + k * 8) % 32), C.packet);
        ctx.restore();
      }
      this.text(verdict, card.x + 236, y + 6, 13, C.text, 1, { font: "mono" });
    };
    row(card.y + 78, "PREFILL", C.cyan, "limited by math", "heat", 0.3);
    row(card.y + 130, "DECODE", C.gold, "limited by memory", "pipe", 0.8);
    ctx.restore();
  }

  private drawHud(t: number, ambient: number) {
    const ctx = this.ctx;
    const tl = this.tl;
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, "rgba(6,10,24,0.95)");
    grad.addColorStop(1, "rgba(6,10,24,0.6)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, 36);
    ctx.fillStyle = rgba(C.frame, 1);
    ctx.fillRect(0, 36, W, 1);

    // Phase badge
    let phase = "INPUT";
    let col = C.label;
    if (t >= tl.tokenizeStart) phase = "TOKENIZE";
    if (t >= tl.prefillBanner) {
      phase = "PREFILL";
      col = C.cyan;
    }
    if (t >= tl.decodeBanner) {
      phase = "DECODE";
      col = C.gold;
    }
    if (t >= tl.endCard) {
      phase = "DONE";
      col = C.text;
    }
    ctx.font = `12px ${this.fonts.pixel}`;
    const bw = ctx.measureText(phase).width + 34;
    this.rect({ x: 14, y: 9, w: bw, h: 20 }, 4, rgba(col, 0.12), rgba(col, 0.8), 1);
    const blink = Math.floor(ambient * 2) % 2 === 0 ? 1 : 0.3;
    ctx.fillStyle = rgba(col, blink);
    ctx.beginPath();
    ctx.arc(26, 19, 3, 0, Math.PI * 2);
    ctx.fill();
    this.text(phase, 36, 24, 12, col, 1, { glow: 6 });

    this.drawModelToggle(ambient);

    const pass = this.currentPass(t);
    if (pass && pass.speed > 1 && t < pass.end && t > pass.dropStart[0]) {
      const last = MODEL_TOGGLE.options[MODEL_TOGGLE.options.length - 1];
      const x = last.x + last.w + 16;
      const fb = Math.floor(ambient * 3) % 2 === 0 ? 1 : 0.45;
      ctx.fillStyle = rgba(C.text, fb);
      for (const dx of [0, 9]) {
        ctx.beginPath();
        ctx.moveTo(x + dx, 13);
        ctx.lineTo(x + dx + 9, 19);
        ctx.lineTo(x + dx, 25);
        ctx.fill();
      }
      this.text(`${pass.speed.toFixed(0)}X`, x + 24, 24, 10, C.text, fb);
    }

    // Layer counter
    const layer = this.layerCounter(t);
    if (layer)
      this.text(`LAYER ${String(layer).padStart(2, "0")}/${LAYER_COUNT}`, W / 2 + 20, 24, 12, C.text, 1, {
        align: "center",
        glow: 6
      });

    // Gauges
    const [compute, memory] = this.gauges(t);
    this.gauge(W - 372, "COMPUTE", compute, C.hot, t);
    this.gauge(W - 186, "MEMORY", memory, C.cyan, t);
  }

  private drawModelToggle(ambient: number) {
    const first = MODEL_TOGGLE.options[0];
    this.text("MODEL", first.x - 7, 23, 9, C.label, 1, { align: "right" });
    const pop = 1 - clamp((ambient - this.switchedAt) / 0.35);
    for (const o of MODEL_TOGGLE.options) {
      const on = o.model === this.model;
      const hover = this.hover === o.model;
      const col = MODEL_COLORS[o.model];
      const grow = on ? pop * 2 : 0;
      const r = { x: o.x - grow, y: MODEL_TOGGLE.y - grow, w: o.w + grow * 2, h: MODEL_TOGGLE.h + grow * 2 };
      if (on) this.neon(r, 4, col, 0.8 + pop);
      this.rect(r, 4, rgba(col, on ? 0.22 : hover ? 0.12 : 0.03), rgba(col, on ? 1 : hover ? 0.8 : 0.35), on ? 1.4 : 1);
      const textCol = on ? mix(col, C.white, 0.55) : hover ? col : mix(col, C.dim, 0.55);
      this.text(o.label, o.x + o.w / 2, 23, 10, textCol, 1, { align: "center", glow: on ? 6 : 0 });
    }
  }

  private gauge(x: number, label: string, v: number, col: RGB, t: number) {
    const ctx = this.ctx;
    this.text(label, x, 23, 10, C.label, 1);
    const bx = x + 64;
    const segs = 10;
    for (let i = 0; i < segs; i++) {
      const on = v * segs > i + 0.25;
      ctx.fillStyle = on ? rgba(mix(col, C.white, i / segs / 2), 1) : rgba(C.cell, 1);
      ctx.fillRect(bx + i * 8, 12, 6, 14);
      if (on && i === Math.floor(v * segs - 0.25)) this.glow.draw(ctx, bx + i * 8 + 3, 19, 10, col, 0.7);
    }
    if (v > 0.85) {
      const blink = Math.floor(t * 5) % 2 === 0;
      this.text("MAX", bx + segs * 8 + 4, 23, 9, blink ? C.red : C.white, 1, { glow: 6 });
    }
  }

  private layerCounter(t: number): number | null {
    const pass = this.currentPass(t);
    if (!pass) return null;
    const runs = pass.runs;
    const l1 = runs.find((r) => r.station === ST_L1);
    const lm = runs.find((r) => r.station === ST_LM);
    if (t < l1.arrive - 0.1 || t >= lm.arrive) return null;
    let value = 1;
    for (const r of runs) {
      if (r.station === ST_EMBED || r.station === ST_LM || t < r.arrive - 0.05) continue;
      if (r.station === ST_L1) value = 1;
      if (r.station === ST_L2) value = 2;
      if (r.station === ST_L3) value = 3;
      if (r.station === ST_WARP) value = Math.min(89, 4 + Math.floor(prog(t, r.arrive, r.leave - r.arrive) * 86));
      if (r.station === ST_L90) value = 90;
    }
    return value;
  }

  /** How busy the math units and the memory pipes are, averaged over the recent past. */
  private gauges(t: number): [number, number] {
    let c = 0;
    let m = 0;
    const n = 8;
    for (let k = 0; k < n; k++) {
      const [ci, mi] = loadAt(this.tl, t - k * 0.07);
      c += ci;
      m += mi;
    }
    const wobble = (seed: number) => 0.03 * noise(t * 7, seed);
    return [clamp(c / n + (c > 0 ? wobble(1) : 0)), clamp(m / n + (m > 0 ? wobble(2) : 0))];
  }
}
